import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowModel,
  type GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { Edit as EditIcon, Image as ImageIcon, Warning as WarningIcon, Autorenew as AutorenewIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useNotifications } from '@/stores/useNotificationStore';
import { formatStockageAllowanceDate } from '@/utils/fiscalYear';
import { buildIarWorksheetReportHtml } from '@/reports/templates/iarWorksheetTemplate';
import { iarService } from '@/services/iar/IARService';
import type {
  IARRunCompletionArtifacts,
  IARRunDetail,
  IARRunLine,
  IARRunStatus,
} from '@/types/iar';
import { getErrorMessage } from '@/utils/httpClient';
import { exportIarRunToXlsx } from '@/utils/iarExport';
import { formatIarRunNumber } from '@/utils/iarRunNumber';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { multiWordMatch } from '@/utils/multiWordMatch';

interface IARRow {
  id: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  location: string;
  locationPath: string;
  recordedQty: number;
  inventoriedQty: number | null;
}

const mapLineToRow = (line: IARRunLine): IARRow => ({
  id: line.id,
  lin: line.lin,
  nsn: line.nsn,
  nomenclature: line.nomen,
  location: line.locationName,
  locationPath: '',
  recordedQty: line.recordedQty,
  inventoriedQty: line.inventoriedQty,
});

const getGain = (recordedQty: number, inventoriedQty: number | null): number => {
  if (inventoriedQty === null) {
    return 0;
  }
  return Math.max(inventoriedQty - recordedQty, 0);
};

const getLoss = (recordedQty: number, inventoriedQty: number | null): number => {
  if (inventoriedQty === null) {
    return 0;
  }
  return Math.max(recordedQty - inventoriedQty, 0);
};

const applySortModel = <T extends object>(data: T[], sortModel: GridSortModel): T[] => {
  const sortItem = sortModel[0];
  if (!sortItem) return data;
  const { field, sort } = sortItem;
  if (!field || !sort) return data;
  const direction = sort === 'desc' ? -1 : 1;
  return [...data].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[field];
    const bVal = (b as Record<string, unknown>)[field];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction;
    if (bVal == null) return -direction;
    if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * direction;
    return String(aVal).localeCompare(String(bVal)) * direction;
  });
};

const normalizeInventoriedQty = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.max(0, Math.trunc(parsed));
};

const buildPrintLoadingHtml = (): string => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>IAR Print</title>
    <style>
      body {
        font-family: "Times New Roman", Georgia, serif;
        margin: 24px;
        color: #111;
      }
    </style>
  </head>
  <body>
    <p>Preparing worksheet preview...</p>
  </body>
</html>`;

const buildPrintErrorHtml = (): string => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>IAR Print Error</title>
    <style>
      body {
        font-family: "Times New Roman", Georgia, serif;
        margin: 24px;
        color: #111;
      }

      .error {
        color: #b00020;
      }
    </style>
  </head>
  <body>
    <h1 class="error">Unable to load print worksheet</h1>
    <p>Close this tab and try again from the IAR run page.</p>
  </body>
</html>`;

export const IARRunPage: React.FC = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const [run, setRun] = useState<IARRunDetail | null>(null);
  const [rows, setRows] = useState<IARRow[]>([]);
  const pendingEditsRef = useRef<Map<string, number | null>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [fatalErrorMessage, setFatalErrorMessage] = useState<string | null>(null);
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [onlyShowDiscrepancies, setOnlyShowDiscrepancies] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);
  const [completionArtifacts, setCompletionArtifacts] = useState<IARRunCompletionArtifacts | null>(
    null,
  );
  const apiRef = useGridApiRef();
  const notifications = useNotifications();

  const runNumber = useMemo(() => {
    const resolvedRunId = run?.id ?? runId ?? '';
    return formatIarRunNumber({
      runId: resolvedRunId,
      runDate: run?.startedAt ?? run?.createdAt ?? null,
    });
  }, [run?.createdAt, run?.id, run?.startedAt, runId]);

  usePageTitle(`IAR Run ${runNumber}`.trim());

  const isViewer = useWriteGuard();
  const runStatus: IARRunStatus = run?.status ?? 'In Progress';
  const isReadOnly =
    isViewer || runStatus !== 'In Progress' || Boolean(run?.completedAt) || Boolean(run?.cancelledAt);

  const loadRun = useCallback(async () => {
    if (!runId) {
      setFatalErrorMessage('No run ID provided');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setFatalErrorMessage(null);

    try {
      const response = await iarService.getRun(runId);
      setRun(response.run);
      setRows(response.lines.map(mapLineToRow));
      if (response.artifacts) {
        setCompletionArtifacts(response.artifacts);
      } else if (response.run.status === 'Complete' || response.run.status === 'Completing') {
        // Fallback: reconstruct minimal artifacts from run fields for
        // runs that were completed before the GET endpoint included artifacts.
        setCompletionArtifacts({
          da444: {
            documentNumber: response.run.da444DocumentNumber ?? null,
            generatedDocumentId: null,
            documentId: null,
            downloadUrl: null,
          },
          discrepancyPackage: null,
          transactionLogId: response.run.transactionLogId ?? null,
        });
      }
    } catch (error) {
      console.error('Failed to load IAR run', error);
      setFatalErrorMessage('Unable to load IAR run. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    void loadRun();
  }, [loadRun]);

  // Poll for run updates while documents are being generated in the background
  useEffect(() => {
    if (run?.status !== 'Completing') {
      return;
    }

    const activePhases = ['document-generation', 'discrepancy-package'];
    if (!run?.completionPhase || !activePhases.includes(run.completionPhase)) {
      return;
    }

    const interval = setInterval(() => {
      void (async () => {
        try {
          const response = await iarService.getRun(runId!);
          setRun(response.run);
          // If completed, stop polling and update artifacts from the response
          if (response.run.status === 'Complete') {
            setRows(response.lines.map(mapLineToRow));
            if (response.artifacts) {
              setCompletionArtifacts(response.artifacts);
            }
          }
        } catch {
          // Silently ignore polling errors
        }
      })();
    }, 10_000);

    return () => clearInterval(interval);
  }, [run?.status, run?.completionPhase, runId]);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'nomenclature', sort: 'asc' },
  ]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return rows.filter((row) => {
      const gain = getGain(row.recordedQty, row.inventoriedQty);
      const loss = getLoss(row.recordedQty, row.inventoriedQty);
      const includeByDiscrepancy = !onlyShowDiscrepancies || gain > 0 || loss > 0;

      if (!includeByDiscrepancy) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable =
        `${row.lin} ${row.nsn} ${row.nomenclature} ${row.location} ${row.locationPath}`;
      return multiWordMatch(searchable, normalizedSearch);
    });
  }, [onlyShowDiscrepancies, rows, searchText]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (sum, row) => ({
          recordedQty: sum.recordedQty + row.recordedQty,
          inventoriedQty: sum.inventoriedQty + (row.inventoriedQty ?? 0),
          gainQty: sum.gainQty + getGain(row.recordedQty, row.inventoriedQty),
          lossQty: sum.lossQty + getLoss(row.recordedQty, row.inventoriedQty),
        }),
        { recordedQty: 0, inventoriedQty: 0, gainQty: 0, lossQty: 0 },
      ),
    [filteredRows],
  );

  const processRowUpdate = useCallback((updatedRow: GridRowModel<IARRow>): IARRow => {
    const sanitizedInventoriedQty = normalizeInventoriedQty(updatedRow.inventoriedQty);
    const nextRow: IARRow = { ...updatedRow, inventoriedQty: sanitizedInventoriedQty };

    // Track the edit in a ref to avoid re-rendering the entire table (which causes scroll bounce)
    pendingEditsRef.current.set(nextRow.id, sanitizedInventoriedQty);
    return nextRow;
  }, []);

  /**
   * Flushes any cells currently in edit mode and saves all inventoried
   * quantities to the server.  Returns the full save response on success, or
   * throws on failure.
   */
  const flushAndSaveEdits = useCallback(async () => {
    if (!runId) {
      throw new Error('No run ID provided');
    }

    const pendingEditedValues = new Map<string, number | null>(pendingEditsRef.current);
    for (const row of filteredRows) {
      const cellMode = apiRef.current.getCellMode(row.id, 'inventoriedQty');
      if (cellMode !== 'edit') {
        continue;
      }

      const pendingRow = apiRef.current.getRowWithUpdatedValues(row.id, 'inventoriedQty') as
        | Partial<IARRow>
        | undefined;

      pendingEditedValues.set(
        row.id,
        normalizeInventoriedQty(pendingRow?.inventoriedQty ?? row.inventoriedQty),
      );
      apiRef.current.stopCellEditMode({ id: row.id, field: 'inventoriedQty' });
    }

    // Merge pending edits into rows state before saving
    if (pendingEditedValues.size > 0) {
      setRows((prevRows) =>
        prevRows.map((row) =>
          pendingEditedValues.has(row.id)
            ? { ...row, inventoriedQty: pendingEditedValues.get(row.id)! }
            : row,
        ),
      );
      pendingEditsRef.current.clear();
    }

    return iarService.saveRunLines(
      runId,
      rows.map((row) => ({
        id: row.id,
        inventoriedQty: pendingEditedValues.get(row.id) ?? row.inventoriedQty,
      })),
    );
  }, [apiRef, filteredRows, rows, runId]);

  const handleRefresh = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    void (async () => {
      setIsRefreshing(true);
      setActionErrorMessage(null);
      try {
        // Save any pending edits before refreshing so unsaved data is not lost.
        try {
          const saveResponse = await flushAndSaveEdits();
          setRun(saveResponse.run);
          setRows(saveResponse.lines.map(mapLineToRow));
        } catch (saveError) {
          console.error('Failed to save pending edits before refresh', saveError);
          setActionErrorMessage(
            `Unable to save pending edits before refresh. ${getErrorMessage(saveError)}`,
          );
          return;
        }

        const response = await iarService.refreshRun(runId);
        setRun(response.run);
        setRows(response.lines.map(mapLineToRow));
      } finally {
        setIsRefreshing(false);
      }
    })();
  }, [flushAndSaveEdits, runId]);

  const handleSave = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    void (async () => {
      setIsSaving(true);
      setActionErrorMessage(null);
      try {
        const saveResponse = await flushAndSaveEdits();
        setRun(saveResponse.run);
        setRows(saveResponse.lines.map(mapLineToRow));
        const savedAt = new Date(saveResponse.run.updatedAt).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
        notifications.success('IAR saved', `Last saved at ${savedAt}`);
      } catch (error) {
        console.error('Failed to save IAR run', error);
        setActionErrorMessage(`Unable to save IAR run. ${getErrorMessage(error)}`);
      } finally {
        setIsSaving(false);
      }
    })();
  }, [flushAndSaveEdits, notifications, runId]);

  const handlePrint = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    if (isReadOnly) {
      setActionErrorMessage('Worksheet printing is only available while the run is In Progress.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) {
      setActionErrorMessage('Unable to open print preview. Please allow pop-ups and try again.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildPrintLoadingHtml());
    printWindow.document.close();

    void (async () => {
      setIsPrinting(true);
      setActionErrorMessage(null);
      try {
        const printPayload = await iarService.getPrintPayload(runId);
        const sortedPrintRows = applySortModel(printPayload.rows, sortModel).map((row, idx) => ({
          ...row,
          sequence: idx + 1,
        }));
        const html = buildIarWorksheetReportHtml({
          runId,
          runNumber,
          status: printPayload.run.status,
          generatedAt: printPayload.generatedAt,
          rows: sortedPrintRows,
          recordedQtyTotal: printPayload.totals.recordedQtyTotal,
        });

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
      } catch (error) {
        printWindow.document.open();
        printWindow.document.write(buildPrintErrorHtml());
        printWindow.document.close();
        console.error('Failed to load IAR print payload', error);
        setActionErrorMessage('Unable to open the print report. Please try again.');
      } finally {
        setIsPrinting(false);
      }
    })();
  }, [isReadOnly, runId, runNumber, sortModel]);

  const handleExport = useCallback(() => {
    setIsExporting(true);
    setActionErrorMessage(null);
    try {
      const sortedRows = applySortModel(rows, sortModel);
      exportIarRunToXlsx(sortedRows, runNumber);
    } catch (error) {
      console.error('Failed to export IAR run', error);
      setActionErrorMessage(`Unable to export IAR run. ${getErrorMessage(error)}`);
    } finally {
      setIsExporting(false);
    }
  }, [rows, runNumber, sortModel]);

  const handleOpenArtifact = useCallback(async (downloadUrl: string | null | undefined) => {
    if (!downloadUrl) {
      return;
    }
    const artifactWindow = preOpenWindow();
    const opened = await openDocumentWithAuth(downloadUrl, artifactWindow);
    if (!opened) {
      setActionErrorMessage(
        'Generated document may have been blocked by your browser. If no tab opened, allow pop-ups and try again.',
      );
    }
  }, []);

  const handleComplete = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    setIsCompleteConfirmOpen(true);
  }, [runId]);

  const handleCancelComplete = useCallback(() => {
    if (isCompleting) {
      return;
    }

    setIsCompleteConfirmOpen(false);
  }, [isCompleting]);

  const handleConfirmComplete = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    void (async () => {
      setIsCompleteConfirmOpen(false);
      setIsCompleting(true);
      setActionErrorMessage(null);
      try {
        // Save any pending edits before completion so backend validation sees current values.
        try {
          const saveResponse = await flushAndSaveEdits();
          setRun(saveResponse.run);
          setRows(saveResponse.lines.map(mapLineToRow));
        } catch (saveError) {
          console.error('Failed to save pending edits before completion', saveError);
          setActionErrorMessage(
            `Unable to save pending edits before completion. ${getErrorMessage(saveError)}`,
          );
          return;
        }

        const completionResult = await iarService.completeRun(runId);
        setRun(completionResult.run);
        setCompletionArtifacts(completionResult.artifacts ?? null);

        // If documents are being generated in the background (202),
        // show a notification and let polling handle the rest.
        if (completionResult.completionWorkflow || completionResult.discrepancyWorkflow) {
          notifications.info('IAR Completing', 'Inventory adjustments applied. Documents are being generated in the background.');
        } else {
          await loadRun();
        }
      } catch (error) {
        console.error('Failed to complete IAR run', error);
        setActionErrorMessage(`Unable to complete IAR run. ${getErrorMessage(error)}`);
      } finally {
        setIsCompleting(false);
      }
    })();
  }, [flushAndSaveEdits, loadRun, notifications, runId]);

  const handleResumeComplete = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    void (async () => {
      setIsCompleting(true);
      setActionErrorMessage(null);
      try {
        const completionResult = await iarService.completeRun(runId);
        setRun(completionResult.run);
        setCompletionArtifacts(completionResult.artifacts ?? null);
        if (completionResult.completionWorkflow || completionResult.discrepancyWorkflow) {
          notifications.info('IAR Completing', 'Completion resumed. Documents are being generated in the background.');
        } else {
          await loadRun();
        }
      } catch (error) {
        console.error('Failed to resume IAR completion', error);
        setActionErrorMessage(`Unable to resume IAR completion. ${getErrorMessage(error)}`);
      } finally {
        setIsCompleting(false);
      }
    })();
  }, [loadRun, notifications, runId]);

  const handleRetryDiscrepancy = useCallback(() => {
    if (!runId) {
      setActionErrorMessage('No run ID provided');
      return;
    }

    void (async () => {
      setIsCompleting(true);
      setActionErrorMessage(null);
      try {
        const retryResult = await iarService.retryDiscrepancy(runId);
        setRun(retryResult.run);
        notifications.info('IAR Retry', 'Discrepancy package generation restarted.');
      } catch (error) {
        console.error('Failed to retry discrepancy package', error);
        setActionErrorMessage(`Unable to retry discrepancy package. ${getErrorMessage(error)}`);
      } finally {
        setIsCompleting(false);
      }
    })();
  }, [notifications, runId]);

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (params.field === 'inventoriedQty' && !isReadOnly) {
        if (params.cellMode !== 'view') {
          return;
        }
        try {
          apiRef.current.startCellEditMode({
            id: params.id,
            field: params.field,
            deleteValue: params.row.inventoriedQty == null,
          });
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          if (!errorMessage.includes('not in view mode')) {
            throw error;
          }
        }
      }
    },
    [apiRef, isReadOnly],
  );

  const isCellEditable = useCallback(
    (params: GridCellParams) => params.field === 'inventoriedQty' && !isReadOnly,
    [isReadOnly],
  );

  const getRowClassName = useCallback((params: { row: IARRow }) => {
    const recorded = params.row.recordedQty;
    const inventoried = params.row.inventoriedQty;
    return recorded === inventoried ? 'iar-row-success-highlight' : '';
  }, []);

  const handleCellKeyDown = useCallback(
    (params: GridCellParams, event: React.KeyboardEvent) => {
      if (params.field !== 'inventoriedQty' || isReadOnly) return;
      if (event.key !== 'Enter') return;

      // Let MUI DataGrid handle Enter natively — it will commit and move to the next row.
      // We only prevent the default MUI behaviour if there is no next row.
    },
    [isReadOnly],
  );

  const columns = useMemo<GridColDef<IARRow>[]>(
    () => [
      {
        field: 'image',
        headerName: 'Image',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: () => (
          <IconButton size="small" title="View item image">
            <ImageIcon fontSize="small" />
          </IconButton>
        ),
      },
      { field: 'lin', headerName: 'LIN', width: 130 },
      { field: 'nsn', headerName: 'NSN', width: 170 },
      { field: 'nomenclature', headerName: 'Nomenclature', flex: 1, minWidth: 220 },
      {
        field: 'location',
        headerName: 'Location',
        width: 160,
        renderCell: (params: GridRenderCellParams<IARRow, string>) => (
          <Stack justifyContent="center" sx={{ height: '100%' }}>
            <Typography variant="body2">{params.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.locationPath}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'recordedQty',
        headerName: 'Recorded Qty',
        type: 'number',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        editable: false,
        description: 'read only',
      },
      {
        field: 'inventoriedQty',
        headerName: 'Inventoried Qty',
        type: 'number',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        editable: !isReadOnly,
        cellClassName: isReadOnly ? '' : 'iar-editable-cell',
        renderCell: (params: GridRenderCellParams<IARRow, number | null>) =>
          params.value === null ? (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ) : (
            params.value
          ),
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Inventoried Qty</span>
            {!isReadOnly && <EditIcon fontSize="small" sx={{ opacity: 0.6 }} />}
          </Stack>
        ),
      },
      {
        field: 'gain',
        headerName: 'Gain',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => getGain(row.recordedQty, row.inventoriedQty),
        cellClassName: (params) => (Number(params.value) > 0 ? 'iar-gain-cell' : ''),
      },
      {
        field: 'loss',
        headerName: 'Loss',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => getLoss(row.recordedQty, row.inventoriedQty),
        cellClassName: (params) => (Number(params.value) > 0 ? 'iar-loss-cell' : ''),
      },
    ],
    [isReadOnly],
  );

  if (isLoading) {
    return (
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 300 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading IAR run...</Typography>
        </Stack>
      </Container>
    );
  }

  if (fatalErrorMessage) {
    return (
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {fatalErrorMessage}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/iar')}>
          Back to IAR Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{ py: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {actionErrorMessage ? <Alert severity="warning">{actionErrorMessage}</Alert> : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography component="h1" variant="h4">
              IAR Run Detail
            </Typography>
            <Typography color="text.secondary">Run #: {runNumber}</Typography>
            <Typography color="text.secondary">Status: {runStatus}</Typography>
            {run?.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Last saved{' '}
                {new Date(run.updatedAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Typography>
            )}
            {run?.stockageAllowanceAsOfDate ? (
              <Typography color="text.secondary">
                Stockage Allowance calculated as of{' '}
                {formatStockageAllowanceDate(new Date(run.stockageAllowanceAsOfDate + 'T00:00:00'))}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="nowrap" alignSelf="flex-start">
            <Button variant="outlined" onClick={() => navigate('/iar')}>
              Back
            </Button>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={isReadOnly || isRefreshing || isSaving || isCompleting}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isReadOnly || isSaving || isRefreshing || isCompleting}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={handlePrint}
              disabled={isReadOnly || isPrinting || isCompleting || isSaving || isRefreshing}
            >
              {isPrinting ? 'Printing...' : 'Print Worksheet'}
            </Button>
            {/* Export is a read-only data extraction — visible to all users including viewers */}
            <Button variant="outlined" onClick={handleExport} disabled={isExporting}>
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleComplete}
              disabled={isReadOnly || isCompleting || isSaving || isRefreshing}
            >
              {isCompleting ? 'Completing...' : 'Complete'}
            </Button>
            {runStatus === 'Completing' && (run?.completionPhase === 'discrepancy-failed' || run?.completionPhase === 'completion-failed') && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleRetryDiscrepancy}
                disabled={isCompleting}
                startIcon={<AutorenewIcon />}
              >
                {isCompleting ? 'Retrying...' : run?.completionPhase === 'completion-failed' ? 'Retry Document Generation' : 'Retry Discrepancy Package'}
              </Button>
            )}
            {runStatus === 'Completing' && run?.completionPhase !== 'discrepancy-failed' && run?.completionPhase !== 'completion-failed' && run?.completionPhase !== 'discrepancy-package' && run?.completionPhase !== 'document-generation' && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleResumeComplete}
                disabled={isCompleting}
              >
                {isCompleting ? 'Resuming...' : 'Resume Completion'}
              </Button>
            )}
          </Stack>
        </Stack>

        {runStatus === 'Completing' && run?.completionPhase === 'document-generation' && (
          <Alert severity="info" icon={<AutorenewIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Documents are being generated in the background.
            </Typography>
            <Typography variant="body2">
              Inventory adjustments have been applied. This page will automatically update when all documents are ready.
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {runStatus === 'Completing' && run?.completionPhase === 'discrepancy-package' && (
          <Alert severity="info" icon={<AutorenewIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Discrepancy package is being generated in the background.
            </Typography>
            <Typography variant="body2">
              Inventory adjustments have been applied. This page will automatically update when the package is ready.
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {runStatus === 'Completing' && run?.completionPhase === 'completion-failed' && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Document generation failed.
            </Typography>
            <Typography variant="body2">
              {run?.completionError ?? 'An unknown error occurred during document generation.'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Inventory adjustments have been applied. Use the &quot;Retry Document Generation&quot; button to try again.
            </Typography>
          </Alert>
        )}

        {runStatus === 'Completing' && run?.completionPhase === 'discrepancy-failed' && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Discrepancy package generation failed.
            </Typography>
            <Typography variant="body2">
              {run?.completionError ?? 'An unknown error occurred during discrepancy package generation.'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Inventory adjustments have been applied. Use the &quot;Retry Discrepancy Package&quot; button to try again.
            </Typography>
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Search / filter (LIN, NSN, Nomenclature, Location)"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={onlyShowDiscrepancies}
                  onChange={(event) => setOnlyShowDiscrepancies(event.target.checked)}
                />
              }
              label="Only Show Discrepancies (gain/loss)"
            />
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 2, flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Box sx={{ flex: 1, minHeight: 0, width: '100%', height: '100%', overflowAnchor: 'none' }}>
            <DataGrid
              apiRef={apiRef}
              rows={filteredRows}
              columns={columns}
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(error) => {
                console.error('Failed to edit Inventoried Qty', error);
              }}
              onCellClick={handleCellClick}
              onCellKeyDown={handleCellKeyDown}
              isCellEditable={isCellEditable}
              getRowClassName={getRowClassName}
              sx={{
                '& .iar-gain-cell': { color: 'warning.main', fontWeight: 600 },
                '& .iar-loss-cell': { color: 'error.main', fontWeight: 600 },
                '& .iar-row-success-highlight .MuiDataGrid-cell': {
                  backgroundColor: 'success.light',
                },
                '& .iar-editable-cell': {
                  backgroundColor: 'action.hover',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
              }}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Typography>
              <strong>Total Recorded Qty:</strong> {totals.recordedQty}
            </Typography>
            <Typography>
              <strong>Total Inventoried Qty:</strong> {totals.inventoriedQty}
            </Typography>
            <Typography color={totals.gainQty > 0 ? 'warning.main' : 'text.primary'}>
              <strong>Total Gain Qty:</strong> {totals.gainQty}
            </Typography>
            <Typography color={totals.lossQty > 0 ? 'error.main' : 'text.primary'}>
              <strong>Total Loss Qty:</strong> {totals.lossQty}
            </Typography>
          </Stack>
        </Paper>

        {completionArtifacts ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6" component="h2">
                Completion Artifacts
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    void handleOpenArtifact(completionArtifacts.da444.downloadUrl);
                  }}
                  disabled={!completionArtifacts.da444.downloadUrl}
                >
                  Open DA444
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    void handleOpenArtifact(completionArtifacts.discrepancyPackage?.downloadUrl);
                  }}
                  disabled={!completionArtifacts.discrepancyPackage?.downloadUrl}
                >
                  {run?.completionPhase === 'discrepancy-package'
                    ? 'Discrepancy Package (Generating...)'
                    : 'Open Discrepancy Package'}
                </Button>
              </Stack>

              {completionArtifacts.transactionLogId ? (
                <Typography color="text.secondary">
                  <strong>Transaction Log ID:</strong> {completionArtifacts.transactionLogId}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

        <Dialog
          open={isCompleteConfirmOpen}
          onClose={handleCancelComplete}
          maxWidth="xs"
          aria-labelledby="iar-complete-confirmation-title"
          aria-describedby="iar-complete-confirmation-description"
        >
          <DialogTitle
            id="iar-complete-confirmation-title"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WarningIcon color="warning" />
            Complete IAR Run
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="iar-complete-confirmation-description">
              Complete this IAR run and apply inventory adjustments? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelComplete} color="inherit" disabled={isCompleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmComplete}
              color="warning"
              variant="contained"
              autoFocus
              disabled={isCompleting}
            >
              {isCompleting ? 'Completing...' : 'Yes, Complete IAR Run'}
            </Button>
          </DialogActions>
        </Dialog>

        {isReadOnly ? (
          <Typography color="text.secondary">
            This run is read-only because status is Complete or Cancelled.
          </Typography>
        ) : null}
      </Stack>
    </Container>
  );
};
